import {
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DayOfWeek, EnforcementSchedule } from "types";
import { DaysOfWeek, formChildGridContainerSpacing } from "utils";
import React, { useCallback, useState } from "react";
import TimeRangeSelect, { TimePattern } from "./TimeRangeSelect";
import dayjs, { Dayjs } from "dayjs";

import { DateRange } from "@mui/lab";
import DaySelect from "./DaySelect";
import { useTranslation } from "react-i18next";

export interface EnforcementSchedulerProps {
  schedule: EnforcementSchedule;
  setSchedule: (schedule: EnforcementSchedule) => void;
  clearSchedule: () => void;
  hideTitle?: boolean;
  disabled?: boolean;
}

const ALL_DAY: DateRange<Dayjs> = [
  dayjs().startOf("day"),
  dayjs().endOf("day"),
];

const EnforcementScheduler: React.FC<EnforcementSchedulerProps> = ({
  schedule,
  setSchedule,
  clearSchedule,
  disabled = false,
  hideTitle = false,
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { t } = useTranslation();
  // state for schedule editor
  const [days, setDays] = useState<DayOfWeek[]>(DaysOfWeek);
  const [time, setTime] = useState<DateRange<Dayjs>>(ALL_DAY);
  const [timePattern, setTimePattern] = useState<TimePattern>(TimePattern.ALL);

  const handleAddToSchedule = useCallback(() => {
    if (!days.length || !time[0] || !time[1]) {
      return;
    }
    const newSchedule = { ...schedule };
    days.forEach((d) => {
      newSchedule[d.toLowerCase() as keyof EnforcementSchedule].push({
        startTime: time[0] ? dayjs(time[0]).format("HHmm") : "",
        endTime: time[1] ? dayjs(time[1]).format("HHmm") : "",
      });
    });
    setSchedule(newSchedule);
    setTimePattern(TimePattern.ALL);
    setDays(DaysOfWeek);
    setTime(ALL_DAY);
  }, [days, schedule, time]);

  return (
    <Grid container width={"100%"} spacing={1.5}>
      {!hideTitle && (
        <Grid size={12}>
          <Divider orientation="horizontal">
            <Typography variant={"overline"} align={"center"}>
              {t("rule.scheduleWidgetTitle")}
            </Typography>
          </Divider>
        </Grid>
      )}
      <Grid
        pb={{ xs: 2, lg: 0 }}
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <DaySelect days={days} setDays={setDays} />
      </Grid>
      <Grid
        py={{ xs: 2, lg: 0 }}
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <TimeRangeSelect
          times={time}
          setTimes={setTime}
          pattern={timePattern}
          setPattern={setTimePattern}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <Stack
          direction={"column"}
          spacing={{ xs: 2, lg: 1 }}
          py={{ xs: 2, lg: 0 }}
        >
          <Button
            variant={"contained"}
            onClick={handleAddToSchedule}
            color={"secondary"}
            size="medium"
            disabled={disabled}
          >
            {t("buttons.add_to_schedule", { ns: "rules" })}
          </Button>
          <Button
            color={"secondary"}
            size={"medium"}
            onClick={() => clearSchedule()}
            variant={mobile ? "contained" : "outlined"}
          >
            {t("clear")}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default EnforcementScheduler;
